import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { preloadContent } from '@confluence/content-body/entry-points/preloadContent';
import { preloadContentHeader } from '@confluence/content-header/entry-points/preloadContentHeader';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites';
import { preloadDraftRedirect } from '@confluence/content-prerequisites/entry-points/preloadDraftRedirect';
import { preloadContentSmartLinks } from '@confluence/content-smartlinks/entry-points/preloadContentSmartLinks';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties';
import { preloadAutohighlights } from '@confluence/contextual-reading-aids/entry-points/preloadAutohighlights';
import { preloadCustomHeaderAndFooter } from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter';
import { preloadMacrosSSR } from '@confluence/fabric-extension-handlers/entry-points/preloadMacrosSSR';
import { preloadConnectWebResources } from '@confluence/fabric-extension-handlers/entry-points/preloadConnectWebResources';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken';
import { preloadIsAttachmentDownloadEnabled } from '@confluence/fabric-media-support/entry-points/preloadIsAttachmentDownloadEnabled';
import { preloadFocusedInlineComment } from '@confluence/inline-comments-queries/entry-points/preloadFocusedInlineComment';
import { preloadAIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/preloadAIFloatingContextMenu';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner';
import {
	COMPANY_HUB_EDIT,
	EDIT_BLOG_V2,
	EDIT_PAGE_V2,
	HUB_EDIT,
} from '@confluence/named-routes/entry-points/routes';
import { preloadEndOfPageRecommendation } from '@confluence/page-recommendations/entry-points/preloadEndOfPageRecommendation';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton';
import { preloadPresenceSettings } from '@confluence/team-presence/entry-points/preloadPresenceSettings';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';
import { preloadPageReactions } from '@confluence/reactions/entry-points/preloadPageReactions';
import type { RouteMatch } from '@confluence/route/entry-points/Route';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points';
import { preloadPageStatusQuery } from '@confluence/query-preloaders-common/entry-points/preloadPageStatusQuery';
import { preloadLegacyBridge } from '@confluence/query-preloaders-common/entry-points/preloadLegacyBridge';
import {
	ATL_PAGE_CONTENT_FOOTER_ACTIONS,
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations';
import { preloadWebPanelLocation } from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { FORGE_MODULE_XEN_MACRO } from '@confluence/forge-ui/entry-points/ForgeModuleType';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery';
import { preloadUseIsAIEnabledForContentQuery } from '@confluence/ai-common/entry-points/preloadUseIsAIEnabledForContentQuery';
import { preloadPageComments } from '@confluence/page-comments-queries/entry-points/preloadPageComments';
import { preloadRequestViewQuery } from '@confluence/no-permission/entry-points/preloadRequestViewQuery';
import { preloadFooterComments } from '@confluence/comments-panel-queries/entry-points/preloadFooterComments';
import { preloadAddCommentPermissionCheck } from '@confluence/comments-panel-queries/entry-points/preloadAddCommentPermissionCheck';
import { preloadLabels } from '@confluence/labels/entry-points/preloadLabels';
import { preloadViewPageOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadViewPageOnboardingState';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation';
import { preloadSuperBatchData } from '@confluence/wrm/entry-points/preloadSuperBatchData';
import {
	getIsEmbeddedConfluence,
	getUsername,
	hasEmbeddedAllowedFeatures,
	shouldPreloadNavigationForTransition,
} from '@confluence/query-preloaders-common/entry-points/matchHelpers';
import { matchBlogPage } from '@confluence/query-preloaders-common/entry-points/matchRoutes';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery';
import { CONFLUENCE_EDITION } from '@confluence/content-header/entry-points/defaultQueryVariables';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { preloadContentTitleWithByLine } from '@confluence/view-page-common/entry-points/preloadContentTitleWithByLine';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode';
import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';

import { UnifiedSpaceQuery } from './queries/UnifiedSpaceQuery.graphql';
import { UnifiedSidebarLinksQuery } from './queries/UnifiedSidebarLinksQuery.graphql';
import { UnifiedUserQuery } from './queries/UnifiedUserQuery.graphql';

const createPreloadContentTask = (
	contentId: string,
	isBlog: boolean,
	spaceKey: string,
	contentNodesPromiseResolve?: (value: any) => void,
): Promise<any> => {
	return prepareGuardExceptionTask('Content', () =>
		preloadContent({
			contentId,
			isBlog,
			spaceKey,
		}).then(({ result, hasErrors }): Promise<any> | void => {
			contentNodesPromiseResolve?.(result?.data?.content?.nodes);

			if (hasErrors) {
				return waitForAllReturnWhenError([
					preloadContentPrerequisites(contentId, spaceKey),
					preloadRequestViewQuery(contentId),
					fg('confluence_redirect_view_page_drafts') &&
						preloadDraftRedirect({
							contentId,
							draftShareId: window.location.search.match(/draftShareId=(.*?)(#|&|$)/)?.[1] ?? '',
						}),
				]);
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			} else if (process.env.REACT_SSR) {
				// No errors & no content means the page may be an unpublished draft
				if (
					result?.data?.content?.nodes?.length === 0 &&
					fg('confluence_redirect_view_page_drafts')
				) {
					return preloadDraftRedirect({
						contentId,
						draftShareId: window.location.search.match(/draftShareId=(.*?)(#|&|$)/)?.[1] ?? '',
					});
				}

				return preloadMacrosSSR({
					contentId,
					useMultipleMacrosQuery: false,
					contentNodes: result?.data?.content?.nodes,
				});
			}
		}),
	);
};

const createContentMainTasks = (params: {
	spaceKey: string;
	contentId: string;
	isLicensed: boolean;
	userId: string | null;
	username?: string;
	edition: ConfluenceEdition | null;
	isPrevEditRoute: boolean;
	isBlog: boolean;
}): Promise<unknown>[] => {
	const { spaceKey, contentId, isLicensed, userId, username, edition, isPrevEditRoute, isBlog } =
		params;

	let contentNodesPromiseResolve: ((value: any) => void) | undefined = undefined;
	const contentNodesPromise = new Promise<any>((resolve) => {
		contentNodesPromiseResolve = resolve;
	});

	return [
		preloadContentHeader({
			spaceKey,
			contentId,
			isLicensed,
			userId,
			username,
			edition,
			isPrevEditRoute,
			useNewContentTopper: true,
		}),
		preloadContentTitleWithByLine(contentId, spaceKey),
		preloadPageTitleContentProperties({ contentId, spaceKey }),
		prepareGuardExceptionTask('Connect Web Resources', () => preloadConnectWebResources()),
		prepareGuardExceptionTask('Macros', () =>
			preloadMacrosSSR({
				contentId,
				useMultipleMacrosQuery: true,
				contentNodes: contentNodesPromise,
			}),
		),
		createPreloadContentTask(contentId, isBlog, spaceKey, contentNodesPromiseResolve),
		preloadPageStatusQuery(contentId),
		preloadLegacyBridge({ contentId, isBlog }),
		preloadReadTime({ contentId, includeContentBody: false, useCache: true }),
		preloadContentSmartLinks(contentId),
	];
};

/*
 * preloadViewPageRoute preload queries are currently arranged in order of latency, with slowest
 * queries pushed to the tasks array first.
 */
export const preloadViewPageRoute = async (
	match: RouteMatch,
	url: string,
	isTransition: boolean = false,
	previousMatch: RouteMatch | null = null,
) => {
	const {
		isLicensed,
		userId,
		edition,
		cloudId,
		accessStatus,
		isLoggedIn,
		environment,
		isAdminHubAIEnabled,
		isRovoEnabled,
		isRovoLLMEnabled,
		activationId,
	} = getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	// following functions will be triggered after we load data
	const postLoadingTasks: (() => Promise<any>)[] = [];

	const { spaceKey, contentId } = match.params;
	const username = getUsername(match);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const isBlog = Boolean(matchBlogPage(url));
	const isInitialLoad = !isTransition;
	const shouldPreloadNavigation =
		!isEmbeddedConfluence && (isInitialLoad || shouldPreloadNavigationForTransition(previousMatch));

	// Get isArchived from cached page data
	const { isArchived } = getCachedPageData(contentId);

	// Don't preload queries that will be updated by editor if previous route is edit_page
	const isPrevEditRoute =
		previousMatch?.name === EDIT_PAGE_V2.name ||
		previousMatch?.name === EDIT_BLOG_V2.name ||
		previousMatch?.name === COMPANY_HUB_EDIT.name ||
		previousMatch?.name === HUB_EDIT.name;

	if (isPrevEditRoute && isTransition) {
		// Preload for edit -> view transition
		return preloadContentHeader({
			spaceKey,
			contentId,
			isLicensed,
			userId,
			username,
			edition,
			isPrevEditRoute,
			useNewContentTopper: true,
		});
	}

	if (expVal('cc_preload_content_first', 'isEnabled', false)) {
		// Content main
		tasks.push(
			createDataFetcherForSuspense('content-main', () =>
				createContentMainTasks({
					spaceKey,
					contentId,
					isLicensed,
					userId,
					username,
					edition,
					isPrevEditRoute,
					isBlog,
				}),
			),
		);
	}

	if (fg('confluence_preload_user_data')) {
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		tasks.push(query({ query: UnifiedUserQuery }));
	}

	// Consolidated preloaders
	if (expVal('confluence_unified_space_preloader', 'isEnabled', false)) {
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		tasks.push(query({ query: UnifiedSpaceQuery, variables: { spaceKey } }));
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		tasks.push(query({ query: UnifiedSidebarLinksQuery, variables: { spaceKey } }));
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		tasks.push(query({ query: UnifiedUserQuery }));
	}

	// Preload superbatch for connect iframes
	if (
		fg('content_early_connect_iframe_initialization') ||
		fg('webpanel_early_connect_iframe_initialization')
	) {
		tasks.push(
			prepareGuardExceptionTask('Superbatch', () =>
				preloadSuperBatchData({ preloadSuperBatchForSSR: true }),
			),
		);
	}

	if (!isPrevEditRoute) {
		tasks.push(preloadPageRestrictionsQuery(contentId, edition === CONFLUENCE_EDITION.FREE));
	}

	// Content types header (object header) + Unified Share and Restrict Button
	tasks.push(
		createDataFetcherForSuspense('content-types-header', () => {
			const contentTypesHeaderPreloadTasks: Promise<any>[] = [];
			if (fg('confluence_frontend_usd_ssr')) {
				contentTypesHeaderPreloadTasks.push(preloadShareAndRestrictButtonQuery(contentId));
			}
			if (fg('confluence_frontend_content_wrapper')) {
				contentTypesHeaderPreloadTasks.push(
					preloadContentTypesHeader(contentId, fg('confluence_frontend_content_wrapper')),
				);

				// preload data for whether "unpublished changes" label should show in view page object header
				contentTypesHeaderPreloadTasks.push(
					preloadWebItemLocation({
						location: 'page.metadata.banner',
						contentId,
					}),
				);
			}
			// (No need to call preloadPageRestrictionsQuery() here; it will be called by
			//  preloadContentHeader() )
			return contentTypesHeaderPreloadTasks;
		}),
	);

	if (!expVal('cc_preload_content_first', 'isEnabled', false)) {
		// Content main
		tasks.push(
			createDataFetcherForSuspense('content-main', () =>
				createContentMainTasks({
					spaceKey,
					contentId,
					isLicensed,
					userId,
					username,
					edition,
					isPrevEditRoute,
					isBlog,
				}),
			),
		);
	}

	if (
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		expValEquals('cc_editor_ai_content_mode', 'variant', 'test') &&
		fg('platform_editor_content_mode_button_mvp')
	) {
		tasks.push(preloadContentMode({ contentId, isDraft: false, useCache: true, isArchived }));
	}

	// Navigation, Page tree and blog tree for blog post
	if (shouldPreloadNavigation) {
		tasks.push(
			createDataFetcherForSuspense('side-navigation', () => {
				return preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog);
			}),
		);
	}

	// Space details
	if (!expVal('confluence_unified_space_preloader', 'isEnabled', false)) {
		tasks.push(preloadSpaceDetail(spaceKey));
	}

	// Inline Comments
	if (match?.query.focusedCommentId) {
		tasks.push(preloadFocusedInlineComment(match?.query.focusedCommentId as string, contentId));
	}

	// Reactions
	if (!isEmbeddedConfluence || hasEmbeddedAllowedFeatures('page-reactions', match)) {
		// Preload whether the server has enabled reactions at all to show placeholder
		tasks.push(preloadWebPanelLocation({ contentId, location: ATL_PAGE_CONTENT_FOOTER_ACTIONS }));
		// Preload reactions asynchronously so SSR won't wait for it
		// it's best effort preloading so if it preloads then we have the data, otherwise SPA can load it
		if (fg('confluence_frontend_content_wrapper')) {
			tasks.push(
				preloadPageReactions({
					pageId: contentId,
					containerId: spaceKey,
				}),
			);
		}
	}

	// Recommendations
	const recommendationsTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_preload_recommendation_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);
	tasks.push(
		slowQueryGuard(
			'preloadEndOfPageRecommendation',
			() => {
				return preloadEndOfPageRecommendation({
					contentId,
					spaceKey,
					contentType: isBlog ? 'blogpost' : 'page',
				});
			},
			recommendationsTimeout,
		),
	);

	// Stuff only needed for initial load
	if (isInitialLoad) {
		tasks.push(
			// We only refresh token when it is about to expire.
			preloadMediaToken(contentId),
		);
		if (!expVal('confluence_unified_space_preloader', 'isEnabled', false)) {
			tasks.push(preloadCustomHeaderAndFooter(spaceKey));
		}
		if (fg('dlp_attachment_download_respects_data_export_dsp')) {
			tasks.push(preloadIsAttachmentDownloadEnabled(contentId, spaceKey));
		}
	}

	// Labels
	if (isTransition) {
		tasks.push(preloadLabels({ contentId }));
	}

	// Preload web panel atl_general
	tasks.push(
		preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
		preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
	);

	// Object Sidebar Control + AI Button
	if (isAdminHubAIEnabled) {
		tasks.push(preloadAIFloatingContextMenu(contentId, isBlog));
	}

	// Preload forge extension manifest
	if (fg('confluence_preload_forge_viewport_heights')) {
		const extensionListLoader = () =>
			preloadUseExtensionList({
				contentId,
				spaceKey,
				moduleType: FORGE_MODULE_XEN_MACRO,
			});

		if (expVal('confluence_unified_space_preloader', 'isEnabled', false)) {
			postLoadingTasks.push(extensionListLoader);
		} else {
			tasks.push(extensionListLoader());
		}
	}

	// Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				cloudId,
				accessStatus,
				isLoggedIn,
				userId,
				environment,
			}),
		);
	}

	// Presence Avatar Visibility
	tasks.push(preloadPresenceSettings({ cloudId, spaceKey }));

	// Preload content-based AI enablement check
	if (fg('confluence_ai_access_policy')) {
		const shouldShowRovo = fg('rovo_use_rovo_llm_enabled_check') ? isRovoLLMEnabled : isRovoEnabled;
		if (isAdminHubAIEnabled || shouldShowRovo) {
			tasks.push(preloadUseIsAIEnabledForContentQuery(contentId));
		}
	}

	// Page Comments
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && fg('confluence_frontend_remove_comments_placeholder')) {
		// this query is not needed for the new redesigned footer comment experience
		if (!fg('confluence_frontend_content_wrapper')) {
			tasks.push(preloadPageComments(contentId));
		}
	}

	// Preload definition autohighlights
	if (fg('confluence_frontend_ssr_autohighlights')) {
		tasks.push(
			preloadAutohighlights({
				contentId,
				cloudId,
				activationId,
				contentType: isBlog ? 'blogpost' : 'page',
			}),
		);
	}

	// Preload footer comments for the redesigned footer comment experience
	// SSRing live pages necessitates non-server (i.e. transition) preloading, so enable here too for consistency in the footer component
	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		(process.env.REACT_SSR || fg('confluence_frontend_content_wrapper')) &&
		fg('confluence_frontend_content_wrapper')
	) {
		// this query is needed for the new redesigned footer comment experience
		if (fg('confluence_frontend_content_wrapper')) {
			tasks.push(preloadFooterComments(contentId, isArchived));
		}
	}

	// Preload add comment permission check for the redesigned footer comment experience
	if (fg('confluence_frontend_content_wrapper')) {
		if (fg('confluence_frontend_content_wrapper')) {
			tasks.push(preloadAddCommentPermissionCheck(contentId));
		}
	}

	// Preload onboarding state for view page
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false)
	) {
		tasks.push(preloadViewPageOnboardingState());
	}

	// Preload Smart Create Feedback Banner
	if (fg('aifc_create_enabled')) {
		tasks.push(preloadSmartCreateFeedbackBanner(contentId));
	}

	const errors = await waitForAllReturnWhenError(tasks);
	const postLoadingErrors = await waitForAllReturnWhenError(postLoadingTasks.map((task) => task()));
	if (errors || postLoadingErrors) {
		return [...(errors || []), ...(postLoadingErrors || [])];
	}
};
